/**
 * Pathway: Shingles (herpes zoster)
 * Population: Adults 18+
 * PGDs: 2a (aciclovir), 2b (valaciclovir)
 */

import type { Pathway } from "../types";
import { links } from "../../linksRegistry";


export const shinglesPathway: Pathway = {
  id: "shingles",
  name: "Shingles (herpes zoster)",
  population:
    "Adults aged 18 years and over with suspected acute herpes zoster (shingles)",
  pgdRefs: [
    "pgd-2a-aciclovir-shingles",
    "pgd-2b-valaciclovir-shingles",
  ],
  links,
  clinicalNotes: [
    "Shingles (herpes zoster) is caused by reactivation of the varicella-zoster virus. Antiviral therapy is most effective when started within 72 hours of rash onset.",
    "Assess immunosuppression status before proceeding — severely immunosuppressed patients require urgent referral and are excluded from both PGDs.",
    "Non-severely immunosuppressed patients may be supplied valaciclovir (PGD 2b) with mandatory GP or out-of-hours notification before the patient leaves.",
  ],
  clinicalNoteLinkRefs: [
    "nice-cks-shingles",
    "nhs-england-pharmacy-first-clinical-pathways",
    "pgd-shingles-aciclovir",
    "pgd-shingles-valaciclovir",
  ],

  demographicConfiguration: {
    bannerFields: [
      { field: "dateOfBirth", label: "Date of birth", type: "date" },
      { field: "ageYears", label: "Age (years)", type: "number" },
      {
        field: "knownAllergies",
        label: "Known allergies",
        type: "string",
        helpText: "Confirm against current consultation",
      },
    ],
  },

  demographicEligibility: {
    minAge: 18,
    blockMessage: "Shingles PGD is only available for adults aged 18 years and over.",
  },

  exclusionPrefillMap: {
    "ex-pregnancy": { from: "isPregnant", equals: true },
    "ex-severely-immuno": { from: "immunoStatus", equals: "immuno_severe" },
  },

  outcomeOverrides: [
    {
      when: { immunoStatus: "immuno_not_severe" },
      outcomeId: "OUT_SUPPLY_IMMUNOSUPPRESSED",
    },
  ],

  entryNode: "q1_age",

  nodes: [
    {
      id: "q1_age",
      type: "yes_no",
      text: "Is the patient aged 18 years or over?",
      hint: "Inferred from patient date of birth where available.",
      required: true,
      // Auto-answer from patient DOB / age. If DOB / age is unknown, the
      // question is shown so the pharmacist can confirm manually.
      autoAnswer: {
        when: { patientAgeAtLeast: 18 },
        value: true,
      },
      yes: "q2_red_flags",
      no: { outcomeId: "OUT_CANNOT_UNDER18" },
    },

    {
      id: "q2_red_flags",
      type: "checklist",
      text: "Any red flag signs requiring urgent escalation?",
      hint: "Tick any that apply. If any are present the consultation should be escalated.",
      showSepsisWarning: true,
      items: [
        {
          id: "ophthalmic",
          text: "Rash involving the eye, tip of the nose, or forehead (suspected ophthalmic shingles)",
        },
        {
          id: "ear-facial",
          text: "Rash involving the ear or facial nerve palsy (suspected Ramsay Hunt syndrome)",
        },
        {
          id: "disseminated",
          text: "Disseminated rash crossing the midline / multiple dermatomes",
        },
        {
          id: "systemic",
          text: "Signs of systemic infection, sepsis or meningitis",
        },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_EMERGENCY",
      noneSelected: "q3_diagnosis",
    },

    {
      id: "GATEWAY_EMERGENCY",
      type: "gateway_alarm",
      heading: "Urgent escalation required",
      body:
        "Red flag features identified. Refer urgently / call 999 if life-threatening. Calculate NEWS2 to support escalation.",
      action: "close",
      triggersPayment: true,
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score (optional)",
      leafletRefs: ["leaflet-shingles-en"],
    },

    {
      id: "q3_diagnosis",
      type: "checklist",
      text: "Confirm clinical features consistent with shingles",
      variant: "confirmation",
      confirmationHeading: "Confirm clinical features consistent with shingles",
      hint: "Tick any that apply — at least one is required to proceed.",
      imageCarousel: {
        title: "View clinical examples",
        images: [
          {
            url: links["shingles-image-1"].url,
            caption: "The blotches become itchy blisters that ooze fluid. A few days later, the blisters dry out and scab.",
          },
          {
            url: links["shingles-image-2"].url,
            caption: "The rash can be red, but this can be harder to see on brown and black skin.",
          },
          {
            url: links["shingles-image-3"].url,
            caption: "The rash can form a cluster that only appears on 1 side of your body. The skin remains painful until after the rash has gone.",
          },
          {
            url: links["shingles-image-4"].url,
            caption: "The rash may be in and around your eye, making it sore and red. It can affect your sight or hearing and make it hard to move 1 side of your face.",
          },
        ],
      },
      items: [
        {
          id: "unilateral-rash",
          text: "Unilateral vesicular rash in a single dermatome",
        },
        {
          id: "prodrome",
          text: "Prodromal pain, tingling, burning or itching at the rash site",
        },
        {
          id: "vesicles",
          text: "Grouped vesicles or crusted lesions on an erythematous base",
        },
        {
          id: "dermatomal-pain",
          text: "Pain localised to a dermatomal distribution",
        },
        {
          id: "no-cross-midline",
          text: "Rash does not cross the midline",
        },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_DIAGNOSIS",
      noneSelected: { outcomeId: "OUT_NOT_SHINGLES" },
    },

    {
      id: "GATEWAY_DIAGNOSIS",
      type: "gateway_progression",
      heading: "Diagnosis confirmed",
      body:
        "Clinical features consistent with shingles. Proceeding to eligibility and treatment assessment.",
      triggersPayment: true,
      next: "q3a_underlying_neuro",
    },

    {
      id: "q3a_underlying_neuro",
      type: "yes_no",
      text: "Does the patient have underlying neurological conditions (e.g. post-herpetic neuralgia, peripheral neuropathy, parkinson's disease)?",
      required: true,
      yes: { outcomeId: "OUT_REFER_UNDERLYING_NEURO" },
      no: "q4_immuno_status",
    },

    {
      id: "q4_immuno_status",
      type: "single_select",
      text: "What is the patient's immunosuppression status?",
      hint:
        "Immunosuppression status drives both eligibility criteria and drug selection — it is asked before rash onset.",
      required: true,
      storesTo: "immunoStatus",
      options: [
        { id: "not_immuno", label: "Not immunosuppressed" },
        {
          id: "immuno_not_severe",
          label: "Non-severely immunosuppressed",
        },
        {
          id: "immuno_severe",
          label: "Severely immunosuppressed",
        },
      ],
      routing: {
        not_immuno: "q5_rash_onset",
        immuno_not_severe: "q5_immuno_rash_within_7d",
        immuno_severe: { outcomeId: "OUT_REFER_SEVERE_IMMUNO" },
      },
    },

    {
      id: "q5_rash_onset",
      type: "single_select",
      text: "When did the rash first appear?",
      required: true,
      storesTo: "rashOnset",
      options: [
        { id: "within_72h", label: "Within the last 72 hours" },
        { id: "72h_to_7d", label: "Between 72 hours and 7 days ago" },
        { id: "over_7d", label: "More than 7 days ago" },
      ],
      routing: {
        within_72h: "q6_criteria_72h",
        "72h_to_7d": "q6_criteria_7d",
        over_7d: { outcomeId: "OUT_NOT_ELIGIBLE_TIMING" },
      },
    },

    {
      id: "q6_criteria_72h",
      type: "checklist",
      text:
        "Within 72 hours of onset — does the patient meet ANY of the following criteria for antiviral treatment?",
      hint: "Tick any that apply.",
      items: [
        { id: "moderate-severe-pain", text: "Moderate to severe pain" },
        { id: "moderate-severe-rash", text: "Moderate to severe rash" },
        { id: "non-truncal", text: "Non-truncal involvement (e.g. limb, head/neck)" },
        { id: "age-50-plus", text: "Patient aged 50 years or older", autoSelectIfAgeAtLeast: 50 },
      ],
      includeNoneOption: true,
      anySelected: "q7_pregnancy",
      noneSelected: { outcomeId: "OUT_NOT_ELIGIBLE_CRITERIA" },
    },

    {
      id: "q6_criteria_7d",
      type: "checklist",
      text:
        "Between 72 hours and 7 days of onset — does the patient meet ANY of the following criteria for antiviral treatment?",
      hint: "Tick any that apply.",
      items: [
        { id: "continued-vesicle", text: "Continued vesicle formation" },
        { id: "severe-pain", text: "Severe pain" },
        { id: "high-risk-complications", text: "High risk of complications" },
        { id: "age-70-plus", text: "Patient aged 70 years or older", autoSelectIfAgeAtLeast: 70 },
      ],
      includeNoneOption: true,
      anySelected: "q7_pregnancy",
      noneSelected: { outcomeId: "OUT_NOT_ELIGIBLE_CRITERIA" },
    },

    {
      id: "q5_immuno_rash_within_7d",
      type: "yes_no",
      text:
        "Did the rash first appear within the last 7 days?",
      hint:
        "For immunosuppressed patients the valaciclovir PGD applies if onset is within 7 days, regardless of dermatome or pain severity.",
      required: true,
      storesTo: "rashWithin7d",
      yes: "q5b_immuno_rash_widespread",
      no: { outcomeId: "OUT_NOT_ELIGIBLE_TIMING" },
    },

    {
      id: "q5b_immuno_rash_widespread",
      type: "yes_no",
      text: "Is the rash widespread or severe?",
      hint:
        "Widespread / severe rash in an immunosuppressed patient excludes them from this PGD — refer to A&E.",
      required: true,
      storesTo: "immunoRashWidespread",
      yes: { outcomeId: "OUT_REFER_IMMUNO_RASH" },
      no: "q5c_immuno_systemically_unwell",
    },

    {
      id: "q5c_immuno_systemically_unwell",
      type: "yes_no",
      text: "Is the patient systemically unwell?",
      hint:
        "Systemic illness (e.g. fever, malaise, signs of sepsis) in an immunosuppressed patient excludes them from this PGD — refer to A&E / call 999.",
      required: true,
      storesTo: "immunoSystemicallyUnwell",
      yes: { outcomeId: "OUT_REFER_IMMUNO_UNWELL" },
      no: "q7_pregnancy",
    },

    {
      id: "q7_pregnancy",
      type: "yes_no",
      text: "Is the patient pregnant?",
      required: true,
      storesTo: "isPregnant",
      // Skip for known-male patients. If sex is unknown, still ask.
      autoAnswer: {
        when: { patientIsFemale: false },
        value: false,
      },
      yes: { outcomeId: "OUT_REFER_PREGNANCY" },
      no: "q8_adherence_risk",
    },

    {
      id: "q8_adherence_risk",
      type: "single_select",
      text: "Is the patient at risk of adherence problems with 5-times-daily dosing?",
      required: true,
      storesTo: "adherenceRisk",
      options: [
        { id: "not_at_risk", label: "Not at risk of adherence problems" },
        {
          id: "at_risk",
          label:
            "Patient is assisted with medications OR taking 8+ medicines daily",
        },
      ],
      routing: {
        not_at_risk: "drug_select",
        at_risk: "drug_select",
      },
    },

    {
      id: "drug_select",
      type: "drug_selection",
      variant: "recommend_with_override",
      rules: [
        {
          when: { immunoStatus: "immuno_not_severe" },
          pgdId: "pgd-2b-valaciclovir-shingles",
          recommendationText:
            "Valaciclovir recommended — immunosuppressed patients are excluded from aciclovir under PGD 2a. GP / out-of-hours notification is mandatory before the patient leaves.",
        },
        {
          when: { adherenceRisk: "at_risk" },
          pgdId: "pgd-2b-valaciclovir-shingles",
          recommendationText:
            "Valaciclovir recommended — simpler dosing (3× daily vs 5× daily) for patients with adherence concerns",
        },
        {
          when: { immunoStatus: "not_immuno", adherenceRisk: "not_at_risk" },
          pgdId: "pgd-2a-aciclovir-shingles",
          recommendationText:
            "Aciclovir recommended — standard first-line treatment",
        },
      ],
      overrideReasons: [
        "Stock unavailable",
        "Patient preference",
        "Clinical judgement (document in notes)",
        "Other",
      ],
      next: "pgd_exclusions",
    },

    {
      id: "pgd_exclusions",
      type: "pgd_exclusion_check",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY" },
      allCleared: { outcomeId: "OUT_SUPPLY" },
    },
  ],

  outcomes: {
    OUT_SUPPLY: {
      id: "OUT_SUPPLY",
      type: "outcome",
      outcomeType: "supply",
      title: "Supply antiviral under shingles PGD",
      reason: "Eligibility criteria met and PGD pre-supply checks cleared.",
      safetyNetting: [
        "Complete the full 7-day course",
        "Maintain good hydration — at least 1.5–2 L of fluid per day",
        "Return if rash spreads, pain worsens, or new neurological symptoms develop",
        "Avoid contact with pregnant women and the immunosuppressed until lesions have crusted",
        "Seek urgent help if signs of systemic infection or eye involvement develop",
      ],
      leafletRefs: ["leaflet-shingles-en"],
      linkRefs: ["nhs-shingles", "bad-shingles-leaflet"],
    },

    OUT_SUPPLY_IMMUNOSUPPRESSED: {
      id: "OUT_SUPPLY_IMMUNOSUPPRESSED",
      type: "outcome",
      outcomeType: "supply",
      title:
        "Supply valaciclovir under shingles PGD 2b — immunosuppressed patient (GP / OOH notification required)",
      reason:
        "Patient is non-severely immunosuppressed and meets PGD 2b inclusion criteria (rash within 7 days, not widespread/severe, systemically well). Aciclovir under PGD 2a is not permitted for immunosuppressed patients. GP — or out-of-hours service if outside GP hours — MUST be contacted to request review and notify of antiviral supply before the patient leaves.",
      safetyNetting: [
        "Complete the full 7-day course",
        "Maintain good hydration",
        "Patient has been advised that GP / specialist review has been requested",
        "Attend A&E or call 999 if symptoms worsen, rash becomes widespread or severe, or the patient becomes systemically unwell",
        "Avoid contact with pregnant women and the immunosuppressed until lesions have crusted",
      ],
      additionalActions: [
        "Call the patient's GP — or send urgent action email — to notify of supply AND request GP review",
        "If outside GP hours, contact the out-of-hours service and document the call",
        "Document who was spoken to, time, and outcome in the consultation record",
      ],
      leafletRefs: ["leaflet-shingles-en"],
      linkRefs: ["nhs-shingles", "bad-shingles-leaflet"],
    },

    OUT_NOT_SHINGLES: {
      id: "OUT_NOT_SHINGLES",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Diagnosis not consistent with shingles",
      reason:
        "Clinical features consistent with shingles were not confirmed. Consider alternative diagnoses.",
      safetyNetting: [
        "Refer to GP or appropriate clinician for further assessment",
        "Return urgently if rash spreads or systemic symptoms develop",
      ],
      leafletRefs: ["leaflet-shingles-en"],
      linkRefs: ["nhs-shingles"],
    },

    OUT_NOT_ELIGIBLE_TIMING: {
      id: "OUT_NOT_ELIGIBLE_TIMING",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Not eligible — rash onset more than 7 days ago",
      reason:
        "Antiviral therapy under this PGD is only effective when initiated within 7 days of rash onset.",
      safetyNetting: [
        "Refer to GP for symptom management and post-herpetic neuralgia review",
        "Advise self-care for pain and rash management in the interim",
        "Return urgently if rash spreads, new neurological symptoms develop, or signs of systemic infection appear",
      ],
      leafletRefs: ["leaflet-shingles-en"],
      linkRefs: ["nhs-shingles"],
    },

    OUT_NOT_ELIGIBLE_CRITERIA: {
      id: "OUT_NOT_ELIGIBLE_CRITERIA",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Not eligible — clinical criteria for antiviral therapy not met",
      reason:
        "Patient does not meet the clinical criteria for antiviral therapy under the PGD.",
      safetyNetting: [
        "Advise self-care: simple analgesia, keep rash clean and dry",
        "Return if pain worsens or rash spreads",
        "Refer to GP if symptoms persist beyond expected duration",
      ],
      leafletRefs: ["leaflet-shingles-en"],
      linkRefs: ["nhs-shingles"],
    },

    OUT_REFER_PREGNANCY: {
      id: "OUT_REFER_PREGNANCY",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Refer — patient is pregnant",
      reason:
        "Aciclovir and valaciclovir are excluded from this PGD in pregnancy. Specialist assessment required.",
      safetyNetting: [
        "Refer to GP / obstetric team same-day for specialist input",
        "Advise the patient to seek urgent review if rash worsens or new symptoms develop",
      ],
      leafletRefs: ["leaflet-shingles-en"],
      linkRefs: ["nhs-shingles"],
    },

    OUT_REFER_SEVERE_IMMUNO: {
      id: "OUT_REFER_SEVERE_IMMUNO",
      type: "outcome",
      outcomeType: "refer_urgent",
      title: "Urgent referral — severely immunosuppressed patient",
      reason:
        "Severely immunosuppressed patients are excluded from both aciclovir (PGD 2a) and valaciclovir (PGD 2b). Urgent specialist assessment required — patient may need IV antivirals.",
      safetyNetting: [
        "Arrange urgent same-day specialist / GP review",
        "Patient may require admission for IV antiviral therapy",
        "Call 999 if signs of disseminated zoster, sepsis or meningitis develop",
      ],
      leafletRefs: ["leaflet-shingles-en"],
    },

    OUT_REFER_IMMUNO_RASH: {
      id: "OUT_REFER_IMMUNO_RASH",
      type: "outcome",
      outcomeType: "refer_urgent",
      title:
        "Refer to A&E — immunosuppressed patient with widespread / severe rash",
      reason:
        "Immunosuppressed patients with a widespread or severe rash are excluded from valaciclovir under PGD 2b. Urgent A&E assessment required — they may need IV antivirals.",
      safetyNetting: [
        "Refer to A&E same-day for assessment",
        "Call 999 if patient deteriorates or shows signs of sepsis",
        "Patient may require admission for IV antiviral therapy",
      ],
      leafletRefs: ["leaflet-shingles-en"],
    },

    OUT_REFER_IMMUNO_UNWELL: {
      id: "OUT_REFER_IMMUNO_UNWELL",
      type: "outcome",
      outcomeType: "refer_urgent",
      title:
        "Refer to A&E — immunosuppressed patient who is systemically unwell",
      reason:
        "Immunosuppressed patients who are systemically unwell are excluded from valaciclovir under PGD 2b. Urgent A&E assessment required.",
      safetyNetting: [
        "Refer to A&E same-day — or call 999 if life-threatening",
        "Patient may require admission for IV antiviral therapy",
        "Calculate NEWS2 to support escalation if appropriate",
      ],
      leafletRefs: ["leaflet-shingles-en"],
    },

    OUT_CANNOT_SUPPLY: {
      id: "OUT_CANNOT_SUPPLY",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply under this PGD — exclusion met",
      reason: "One or more PGD pre-supply exclusions were triggered.",
      safetyNetting: [
        "Refer to GP or appropriate prescriber for assessment",
        "Document the exclusion(s) that prevented supply",
        "Return urgently if symptoms worsen",
      ],
      leafletRefs: ["leaflet-shingles-en"],
      linkRefs: ["nhs-shingles"],
    },

    OUT_CANNOT_UNDER18: {
      id: "OUT_CANNOT_UNDER18",
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — patient under 18",
      reason: "This PGD is for adults aged 18 years and over.",
      safetyNetting: [
        "Refer to GP for assessment",
        "Advise self-care and safety netting in the interim",
      ],
      leafletRefs: ["leaflet-shingles-en"],
    },

    OUT_REFER_UNDERLYING_NEURO: {
      id: "OUT_REFER_UNDERLYING_NEURO",
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Refer — Underlying neurological conditions",
      reason: "Patients with underlying neurological conditions may be at higher risk of complications and should be referred for specialist assessment.",
      safetyNetting: [
        "Refer to GP or appropriate clinician for assessment",
        "Advise self-care and safety netting in the interim",
      ],
      leafletRefs: ["leaflet-shingles-en"],
    }
  },
};
